
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/authContext";
import { ItemWishlist, WishlistItem } from "@/components/ItemWishlist";
import { PostComposerModal } from "@/components/PostComposerModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Loader2, User, ArrowLeft, Share2, Bookmark,
    MessageCircle, Mail, Copy, Check, Users, Eye
} from "lucide-react";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";

import { getToken } from "@/lib/authContext";
import { cn } from "@/lib/utils";
import { useLocation } from "wouter";
import { useI18n } from "@/lib/i18n";
import { useToast } from "@/hooks/use-toast";
import { LoginModal } from "@/components/LoginModal";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { X } from "lucide-react";

interface UserProfile {
    id: string;
    username: string;
    avatarUrl: string | null;
    isInsider: boolean;
}

export default function WishlistPage() {
    const { user } = useAuth();
    const [_, setLocation] = useLocation();
    const { t } = useI18n();
    const { toast } = useToast();

    const { data: myItems, isLoading: isLoadingMyItems } = useQuery({
        queryKey: ["my-wishlist"],
        queryFn: async () => {
            const cacheKey = `wishlist_my_items_cache_${user?.id || "anon"}`;
            const token = await getToken();
            try {
                const res = await fetch("/api/wishlist/my-items", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch my wishlist");
                const items = await res.json() as WishlistItem[];

                if (typeof window !== "undefined") {
                    try {
                        window.localStorage.setItem(cacheKey, JSON.stringify(items));
                    } catch {
                        // Ignore cache write failures.
                    }
                }

                return items;
            } catch (error) {
                if (typeof window !== "undefined") {
                    try {
                        const raw = window.localStorage.getItem(cacheKey);
                        if (raw) {
                            const cached = JSON.parse(raw) as WishlistItem[];
                            return Array.isArray(cached) ? cached : [];
                        }
                    } catch {
                        // Ignore cache read failures and rethrow original error.
                    }
                }
                throw error;
            }
        },
        enabled: !!user,
    });

    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalIsSignUp, setAuthModalIsSignUp] = useState(false);

    if (!user) {
        return (
            <div className="min-h-screen bg-background flex flex-col pb-20 md:pb-0">
                <main className="flex-1 container mx-auto px-4 py-16 flex flex-col items-center justify-center max-w-md text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                        <Bookmark className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold mb-4">{t("wishlist.loginRequired")}</h1>
                    <p className="text-muted-foreground mb-8 text-lg">
                        {t("wishlist.loginRequiredDesc")}
                    </p>
                    <div className="flex flex-col gap-3 w-full">
                        <Button
                            size="lg"
                            className="w-full text-lg h-14 font-bold"
                            onClick={() => {
                                setAuthModalIsSignUp(false);
                                setShowAuthModal(true);
                            }}
                        >
                            {t("nav.login")}
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            className="w-full text-lg h-14 font-medium"
                            onClick={() => {
                                setAuthModalIsSignUp(true);
                                setShowAuthModal(true);
                            }}
                        >
                            {t("loginModal.createAccount")}
                        </Button>
                    </div>
                </main>

                <LoginModal
                    open={showAuthModal}
                    onOpenChange={setShowAuthModal}
                    initialSignUp={authModalIsSignUp}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col pb-20 md:pb-0">
            <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
                <div className="flex flex-col gap-2 mb-6">
                    <h1 className="text-3xl font-bold tracking-tight">{t("wishlist.title")}</h1>
                    <p className="text-muted-foreground text-sm">{t("wishlist.subtitle")}</p>
                </div>

                <Tabs defaultValue="mine" className="space-y-6">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="mine">{t("wishlist.mineTab")}</TabsTrigger>
                        <TabsTrigger value="following">{t("wishlist.followingTab")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="mine" className="space-y-4">
                        {/* Share / Invite section */}
                        <InviteFriendsCard />

                        <Card>
                            <CardHeader>
                                <CardTitle>{t("wishlist.mySearchTitle")}</CardTitle>
                                <CardDescription>{t("wishlist.mySearchDesc")}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {isLoadingMyItems ? (
                                    <div className="flex justify-center py-8">
                                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <ItemWishlist items={myItems || []} />
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="following">
                        <FollowingList />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// Invite Friends Card
// ════════════════════════════════════════════════════════════════════
function InviteFriendsCard() {
    const { t } = useI18n();
    const { user } = useAuth();
    const { toast } = useToast();
    const [copied, setCopied] = useState(false);
    const [shareUrl, setShareUrl] = useState("");

    const { data: tokenData } = useQuery({
        queryKey: ["wishlist-share-token"],
        queryFn: async () => {
            const token = await getToken();
            const res = await fetch("/api/wishlist/my-share-token", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to get share token");
            return res.json() as Promise<{ token: string }>;
        },
    });

    useEffect(() => {
        if (tokenData?.token) {
            const base = window.location.origin;
            setShareUrl(`${base}/wishlist/shared/${tokenData.token}`);
        }
    }, [tokenData?.token]);

    const { data: followersData } = useQuery({
        queryKey: ["wishlist-followers-count"],
        queryFn: async () => {
            const token = await getToken();
            const res = await fetch("/api/wishlist/followers-count", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return { count: 0 };
            return res.json() as Promise<{ count: number }>;
        },
    });

    const handleCopy = async () => {
        if (!shareUrl) return;
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast({ title: t("wishlist.linkCopied"), duration: 2000 });
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsApp = () => {
        if (!shareUrl) return;
        const text = encodeURIComponent(t("wishlist.shareMessage", { url: shareUrl }));
        window.open(`https://wa.me/?text=${text}`, "_blank");
    };

    const handleEmail = () => {
        if (!shareUrl) return;
        const subject = encodeURIComponent(t("wishlist.sharedTitle", { name: user?.username || t("common.anonymous") }));
        const body = encodeURIComponent(t("wishlist.shareMessage", { url: shareUrl }));
        window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
    };

    return (
        <Card className="border-primary/20 bg-primary/5">
            <CardContent className="py-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Share2 className="h-4 w-4 text-primary" />
                        <span className="font-semibold text-sm">{t("wishlist.inviteFriends")}</span>
                    </div>
                    {followersData && followersData.count > 0 && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {t("wishlist.followers", { count: followersData.count })}
                        </span>
                    )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">{t("wishlist.inviteDesc")}</p>
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="default" className="gap-1.5 text-xs" onClick={handleWhatsApp}>
                        <MessageCircle className="h-3.5 w-3.5" />
                        {t("wishlist.shareViaWhatsApp")}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={handleEmail}>
                        <Mail className="h-3.5 w-3.5" />
                        {t("wishlist.shareViaEmail")}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={handleCopy}>
                        {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                        {t("wishlist.copyLink")}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// ════════════════════════════════════════════════════════════════════
// Following List (people whose wishlists you follow)
// ════════════════════════════════════════════════════════════════════
function FollowingList() {
    const { t } = useI18n();
    const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
    const [userToUnfollow, setUserToUnfollow] = useState<UserProfile | null>(null);
    const queryClient = useQueryClient();

    const { data: following, isLoading } = useQuery({
        queryKey: ["wishlist-following"],
        queryFn: async () => {
            const token = await getToken();
            const res = await fetch("/api/wishlist/following", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch following");
            return res.json() as Promise<(UserProfile & { followedAt: string })[]>;
        },
    });

    const unfollowMutation = useMutation({
        mutationFn: async (userId: string) => {
            const token = await getToken();
            const res = await fetch(`/api/wishlist/follow/${userId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Unfollow failed");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["wishlist-following"] });
            setUserToUnfollow(null);
        },
    });

    if (selectedUser) {
        return <FriendWishlistDetail user={selectedUser} onBack={() => setSelectedUser(null)} />;
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> {t("wishlist.followingTab")}</CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : following && following.length > 0 ? (
                        <div className="space-y-3">
                            {following.map((user) => (
                                <div
                                    key={user.id}
                                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                                >
                                    <Avatar className="h-10 w-10">
                                        <AvatarImage src={user.avatarUrl || undefined} />
                                        <AvatarFallback>{(user.username || "?").substring(0, 2).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold truncate">{t("wishlist.sharedTitle", { name: user.username })}</p>
                                        <div className="flex items-center gap-2">
                                            {user.isInsider && (
                                                <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">{t("account.tier.insider")}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => setSelectedUser(user)}
                                            className="h-8 text-xs"
                                        >
                                            {t("wishlist.view")}
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setUserToUnfollow(user)}
                                            className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground py-8 text-sm">{t("wishlist.followingEmpty")}</p>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!userToUnfollow} onOpenChange={(open) => !open && setUserToUnfollow(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("wishlist.unfollowConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("wishlist.unfollowConfirmDesc", { name: userToUnfollow?.username || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("shops.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => userToUnfollow && unfollowMutation.mutate(userToUnfollow.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t("wishlist.unfollow")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// ════════════════════════════════════════════════════════════════════
// Friend Wishlist Detail (viewing a specific friend's items)
// ════════════════════════════════════════════════════════════════════
function FriendWishlistDetail({ user: friendUser, onBack, onUnfollow }: { user: UserProfile; onBack: () => void; onUnfollow?: () => void }) {
    const { t } = useI18n();

    const { data: userItems, isLoading } = useQuery({
        queryKey: ["user-items", friendUser.id],
        queryFn: async () => {
            const token = await getToken();
            const res = await fetch(`/api/wishlist/users/${friendUser.id}/items`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch user items");
            return res.json() as Promise<WishlistItem[]>;
        },
    });

    const handleFoundForFriend = (item: WishlistItem) => {
        const message = encodeURIComponent(`Hoi ${friendUser.username}, ik heb de ${item.item_name} voor je gevonden!`);
        window.open(`https://wa.me/?text=${message}`, "_blank");
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={onBack} className="gap-2 pl-0">
                    <ArrowLeft className="h-4 w-4" />
                    {t("wishlist.backToSearchResults")}
                </Button>
                {onUnfollow && (
                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={onUnfollow}>
                        {t("wishlist.unfollow")}
                    </Button>
                )}
            </div>

            <Card>
                <CardHeader className="flex flex-row items-center gap-4">
                    <Avatar className="h-12 w-12">
                        <AvatarImage src={friendUser.avatarUrl || undefined} />
                        <AvatarFallback>{(friendUser.username || "?").substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                        <CardTitle>{t("wishlist.sharedTitle", { name: friendUser.username })}</CardTitle>
                        <CardDescription>{t("wishlist.helpSearch", { name: friendUser.username })}</CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : userItems && userItems.length > 0 ? (
                        <ItemWishlist items={userItems} readonly onFoundForFriend={handleFoundForFriend} />
                    ) : (
                        <p className="text-muted-foreground text-center py-8">
                            {t("wishlist.userNoItems", { name: friendUser.username })}
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}


